import { useContext, useEffect, useRef, useCallback } from "react"
import { MessageBrokerContext } from "src/global/messageBroker/context"
import { Action, EventActionConfig, MessageBrokerContextType } from "src/global/messageBroker/types"
import { EventBuffer } from "src/global/messageBroker/event-buffer"
import { EventType } from "src/types/events"

/**
 * Hook to subscribe to message broker events. Automatically handles cleanup on unmount.
 * @param event - The event name to subscribe to
 * @param action - The action to execute when the event is received
 * @param config - Optional configuration for filtering events and buffering
 * @returns The message broker context
 * @throws Error if used outside of MessageBrokerProvider
 */
export function useEventAction(
  event: EventType,
  action: Action,
  config: EventActionConfig = {},
): MessageBrokerContextType {
  const context = useContext(MessageBrokerContext)
  if (!context) {
    throw new Error("useEventAction must be used within a MessageBrokerProvider")
  }

  const { registerActionForEvent, unregisterActionForEvent } = context
  const { filterByEntityId, bufferDelay } = config

  const eventBufferRef = useRef<EventBuffer | null>(null)

  const wrappedAction = useCallback<Action>(
    (message) => {
      if (filterByEntityId && message.entityId !== filterByEntityId) {
        return
      }

      if (eventBufferRef.current) {
        eventBufferRef.current.add(message)
      } else {
        action(message)
      }
    },
    [action, filterByEntityId],
  )

  useEffect(() => {
    if (bufferDelay !== undefined) {
      eventBufferRef.current = new EventBuffer(action, bufferDelay)
    }

    registerActionForEvent(event, wrappedAction)

    return () => {
      if (eventBufferRef.current) {
        eventBufferRef.current.clear()
      }
      unregisterActionForEvent(event, wrappedAction)
    }
  }, [event, wrappedAction, bufferDelay, registerActionForEvent, unregisterActionForEvent, action])

  return context
}
